import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Button } from '@ipfabric/ui-components/Button/Button';
import { withTargetValue } from '@ipfabric/ui-components/utils/events/withTargetValue';
import {
  type TableColumn,
  type TableFilterOperator,
  createLabelsFilter,
  filterOperators,
  isArrayContainsOperator,
  isAsnTableFilter,
  isBooleanTableFilter,
  isDateTimeTableFilter,
  isLabelsArrayOperator,
  isLabelsTableFilter,
  isNumberTableFilter,
  isObjectTableFilter,
  isSelectTableFilter,
} from '@ipfabric/utils';
import { nullable } from '@ipfabric/utils/fp/nullable';
import type { RequiredBy } from '@ipfabric/utils/types/RequiredBy';
import classNames from 'classnames';
import { FormInputError } from 'components/FormInputError/FormInputError';
import {
  Select,
  makeGetSelectOptionValueFromObject,
} from 'components/Select/Select';
import type { SelectOptionType } from 'components/Select/SelectOption';
import { useRuleContext } from 'components/Table/components/AdvancedFilter/useRuleContext';
import { TextInput } from 'components/TextInput/TextInput';
import { useLocale } from 'containers/App/LocaleContext';
import { pipe } from 'fp-ts/lib/function';
import { useMemo } from 'react';
import type { JSX } from 'react';
import type { AdvancedFilterColumnRecord } from './AdvancedFilter';
import { ColorFilterRule } from './types/ColorFilterRule';
import { ColumnFilterRule } from './types/ColumnFilterRule';
import { DateTimeFilterForNowRule } from './types/DateTimeFilterForNowRule';
import { DateTimeFilterRule } from './types/DateTimeFilterRule';
import { LabelsColumnFilterRule } from './types/LabelsColumnFilterRule';
import { SelectFilterRule } from './types/SelectFilterRule';
import { TextFilterRule } from './types/TextFilterRule';
import {
  type GroupFilter,
  type RuleFilter,
  type RuleFilterColumn,
  emptyRuleFilter,
} from './useAdvancedFilter';
import { validateRule } from './validateRule';

interface Props<TRow> {
  filter: RuleFilter;
  path: number[];
  onChange: (path: number[], newFilter: GroupFilter | RuleFilter) => void;
  onDelete: (path: number[]) => void;
  columns: AdvancedFilterColumnRecord<TRow>;
  /** group is draggable iff it is nested */
  draggable?: boolean;
  disableColorOperators?: boolean;
}

const textOperators = [
  ...filterOperators.text,
  ...filterOperators.number,
  ...filterOperators.select,
  ...filterOperators.regex,
  ...filterOperators.routing,
  ...filterOperators.ip,
];

const topLevelOperators = [
  ...filterOperators.empty,
  ...filterOperators.color,
  ...filterOperators.arrayContains,
];
type TopLevelOperator = (typeof topLevelOperators)[number];
const arrayOperators = ['any', 'all'] as const;

type ArrayOperator = (typeof arrayOperators)[number];

/**
 * Combine top-level operators (empty, color) with array operators (any, all) on the same level.
 *
 * NIM-19138 has revealed that until now there was no option to filter empty values or by intent result
 * on array columns via advanced filter. We have implemented a way of combining those in one dropdown,
 * but the logic gets more and more twisted. It is advised to revisit this in NIM-14783 and refactor
 * the code to make it more readable and covered by tests.
 */
type ArrayLevelOperator = TopLevelOperator | ArrayOperator;

const areOperatorsCompatible = (
  o1: string | null,
  o2: string | null,
): boolean =>
  // we can use single condition for text operators (text, number, select, etc.) because on column change, data type
  // remains same
  (textOperators.some((o) => o === o1) &&
    textOperators.some((o) => o === o2)) ||
  (filterOperators.color.some((o) => o === o1) &&
    filterOperators.color.some((o) => o === o2)) ||
  (filterOperators.column.some((o) => o === o1) &&
    filterOperators.column.some((o) => o === o2));

const isTopLevelOperator = (
  operator?: TableFilterOperator | ArrayLevelOperator | null,
): operator is TopLevelOperator =>
  // type assertion is required due to missing subtype support on includes()
  topLevelOperators.includes(operator as TopLevelOperator);

const isLabelsFilterColumn = (
  column: RuleFilterColumn | null | undefined,
): boolean =>
  column !== null && column !== undefined && isLabelsTableFilter(column.filter);

const isLabelsArrayDropdownOperator = (
  operator: string | null | undefined,
): operator is ArrayLevelOperator =>
  isTopLevelOperator(
    operator as TableFilterOperator | ArrayLevelOperator | null,
  ) || isLabelsArrayOperator(operator);

export const FilterRule = <TRow,>(props: Props<TRow>): JSX.Element => {
  const { advancedFilter: advancedFilterMsg } = useLocale();

  const ruleContext = useRuleContext();

  const getDynamicFieldError = (
    column: RuleFilterColumn | null,
    dynamicField: string,
  ): string | undefined => {
    if (column?.key !== 'device.attributes') {
      // keep hardcoded until there is only one kind of such key
      return undefined;
    }
    if (dynamicField === '') {
      return 'Required';
    }
    if (!dynamicField.match(/^\w+$/)) {
      return 'Only letters, numbers and _ are allowed';
    }
    return undefined;
  };

  const columns: SelectOptionType<RuleFilterColumn>[] = Object.values(
    props.columns,
  )
    .filter(
      (column): column is RequiredBy<TableColumn<TRow>, 'filter'> =>
        column.filter !== undefined,
    )
    .reduce<SelectOptionType<RuleFilterColumn>[]>((acc, column) => {
      acc.push({
        label: column.title,
        value: { key: column.key, filter: column.filter },
      });

      if (isObjectTableFilter(column.filter)) {
        for (const child of column.filter.children) {
          acc.push({
            label: `${column.title} - ${child.title}`,
            value: {
              key: child.field,
              filter: child.filter,
              parent: column.key,
            },
          });
        }
      }

      return acc;
    }, []);

  const isArrayColumn = (ruleColumn: RuleFilterColumn | null): boolean => {
    if (!ruleColumn) {
      return false;
    }

    const column = props.columns[ruleColumn.key];
    const parentColumn = ruleColumn.parent
      ? props.columns[ruleColumn.parent]
      : undefined;
    const array = column?.array || parentColumn?.array;

    // object column itself does not work as array even if it actually is array object
    return (column && column.filter?.type !== 'object') || !column
      ? !!array
      : false;
  };

  const findFilter = (ruleColumn: RuleFilterColumn | null) => {
    if (!ruleColumn) {
      return undefined;
    }

    if (ruleColumn.parent) {
      // Fixed noUncheckedIndexedAccess NIM-19408
      const { filter } = props.columns[ruleColumn.parent]!;

      return isObjectTableFilter(filter)
        ? filter.children.find(({ field }) => field === ruleColumn.key)?.filter
        : undefined;
    }
    return props.columns[ruleColumn.key]?.filter;
  };

  const handleColumnChange = (value: RuleFilterColumn | null) => {
    const newColumnFilter = findFilter(value);
    const preserveValue =
      newColumnFilter &&
      isArrayColumn(value) === isArrayColumn(props.filter.column) &&
      newColumnFilter.type === props.filter.column?.filter.type &&
      // We can expect there will be most likely no overlap in select values
      newColumnFilter.type !== 'select';

    const nextFilterBase = preserveValue ? props.filter : emptyRuleFilter;

    // Labels use dedicated top-level operators (includes / includes name), not
    // generic "any item / all items" array quantification.
    if (isLabelsFilterColumn(value)) {
      props.onChange(props.path, {
        ...emptyRuleFilter,
        column: value,
        operator: 'containsAny',
        arrayOperator: undefined,
        dynamicFieldError: getDynamicFieldError(
          value,
          emptyRuleFilter.dynamicField,
        ),
      });
      return;
    }

    props.onChange(props.path, {
      // will be intentionally overriden by nextFilterBase if prev value is preserved
      arrayOperator: isArrayColumn(value) ? 'any' : undefined,
      ...nextFilterBase,
      column: value,
      dynamicFieldError: getDynamicFieldError(
        value,
        nextFilterBase.dynamicField,
      ),
    });
  };

  const handleOperatorChange = (
    value: TableFilterOperator | null,
    options?: {
      arrayOperator?: ArrayOperator;
    },
  ) => {
    const rule = {
      ...emptyRuleFilter,
      operator: value,
      arrayOperator:
        'arrayOperator' in (options ?? {})
          ? options?.arrayOperator
          : props.filter.arrayOperator,
      dynamicField: props.filter.dynamicField,
      dynamicFieldError: props.filter.dynamicFieldError,
      column: props.filter.column,
      value: areOperatorsCompatible(props.filter.operator, value)
        ? props.filter.value
        : emptyRuleFilter.value,
      unsupportedOperatorError: undefined,
    };

    props.onChange(props.path, {
      ...rule,
      error: props.filter.column
        ? (isLabelsFilterColumn(props.filter.column)
            ? validateRule.labels
            : validateRule[props.filter.column.filter.type])?.(
            rule,
            ruleContext,
          )
        : undefined,
    });
  };

  const handleArrayOperatorChange = (value: ArrayLevelOperator | null) => {
    if (value === null) {
      stumble('unexpected null returned from mandatory select');
      return;
    }
    if (isLabelsArrayDropdownOperator(value)) {
      handleOperatorChange(value as TableFilterOperator, {
        arrayOperator: undefined,
      });
      return;
    }
    if (props.filter.arrayOperator === undefined) {
      // changing from top-level operator to array operator
      handleOperatorChange(null, {
        arrayOperator: value,
      });
      return;
    }

    // simple change between array operators
    const rule = {
      ...props.filter,
      arrayOperator: value ?? 'any',
    };

    props.onChange(props.path, {
      ...rule,
      error: props.filter.column
        ? (isLabelsFilterColumn(props.filter.column)
            ? validateRule.labels
            : validateRule[props.filter.column.filter.type])?.(
            rule,
            ruleContext,
          )
        : undefined,
    });
  };

  const handleValueChange = (value: string) => {
    const updatedRule = {
      ...props.filter,
      value,
    };
    props.onChange(props.path, {
      ...updatedRule,
      error:
        updatedRule.column?.filter.type !== undefined
          ? (isLabelsFilterColumn(updatedRule.column)
              ? validateRule.labels
              : validateRule[updatedRule.column.filter.type])?.(
              updatedRule,
              ruleContext,
            )
          : undefined,
    });
  };

  const handleDynamicFieldChange = (dynamicField: string) => {
    const updatedRule = {
      ...props.filter,
      dynamicField,
    };
    props.onChange(props.path, {
      ...updatedRule,
      dynamicFieldError: getDynamicFieldError(
        props.filter.column,
        dynamicField,
      ),
    });
  };

  const handleBlur = () => {
    props.onChange(props.path, {
      ...props.filter,
      touched: true,
    });
  };

  const arrayOperators: SelectOptionType<ArrayLevelOperator>[] = useMemo(() => {
    const column = props.filter.column;
    if (column !== null && isLabelsFilterColumn(column)) {
      const labelsOperators = isLabelsTableFilter(column.filter)
        ? column.filter.operators
        : createLabelsFilter().operators;

      return labelsOperators
        .filter(
          (operator) =>
            isLabelsArrayOperator(operator) ||
            filterOperators.empty.some((emptyOp) => emptyOp === operator),
        )
        .map((operator) => ({
          label: advancedFilterMsg.operators[operator] ?? operator,
          value: operator as ArrayLevelOperator,
        }));
    }

    return [
      ...(props.filter.column?.filter.operators ?? [])
        .filter((operator) => isArrayContainsOperator(operator))
        .map((operator) => ({
          label: advancedFilterMsg.operators[operator] ?? operator,
          value: operator,
        })),
      {
        label: advancedFilterMsg.arrayOperators.any,
        value: 'any',
      },
      {
        label: advancedFilterMsg.arrayOperators.all,
        value: 'all',
      },
      {
        label: advancedFilterMsg.operators.emptyTrue,
        value: 'emptyTrue',
      },
      {
        label: advancedFilterMsg.operators.emptyFalse,
        value: 'emptyFalse',
      },
      ...(props.disableColorOperators
        ? []
        : [
            {
              label: advancedFilterMsg.operators.color,
              value: 'color' as const,
            },
          ]),
    ];
  }, [advancedFilterMsg, props.disableColorOperators, props.filter.column]);

  const hideOperatorSelectForArrayTopLevelOperator =
    (isArrayColumn(props.filter.column) &&
      isLabelsFilterColumn(props.filter.column)) ||
    // Array column
    (isArrayColumn(props.filter.column) &&
      props.filter.arrayOperator === undefined &&
      props.filter.operator !== null &&
      isTopLevelOperator(props.filter.operator));

  const operators: SelectOptionType<TableFilterOperator>[] | null =
    useMemo(() => {
      if (!props.filter.column) {
        return null;
      }

      return (
        props.filter.column.filter.operators
          // TODO NIM-14245: Remove filtering when now operator is implemented
          .filter((o: any) => o !== 'now')
          .map((o) => {
            if (!(o in advancedFilterMsg.operators)) {
              stumble(`Missing locale for operator: ${o}`);
            }
            return {
              label: advancedFilterMsg.operators[o] ?? o,
              value: o,
            };
          })
          .filter(
            ({ value }) => !(props.disableColorOperators && value === 'color'),
          )
      );
    }, [props.filter, props.disableColorOperators, advancedFilterMsg]);

  const valueErrorToRender = props.filter.touched
    ? props.filter.error
    : undefined;

  const resolvedOperator =
    props.filter.operator ??
    (isLabelsArrayOperator(props.filter.arrayOperator)
      ? (props.filter.arrayOperator as TableFilterOperator)
      : null);

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: TBD eventually cleanup
  const getFilter = () => {
    const commonFilterProps = {
      hasError: !!valueErrorToRender,
      onValueChange: handleValueChange,
      onBlur: handleBlur,
      value: props.filter.value,
    };

    // normally, filter would be chosen based on props.filter.column.filter > props.filter.operator, but after
    // discussion, we decided to simplify it to avoid insanely large & repetitive if-else tree
    const operator = resolvedOperator;

    if (!props.filter.column || !operator) {
      return null;
    }

    if (textOperators.some((o) => o === operator) || operator === 'cidr') {
      if (
        isSelectTableFilter(props.filter.column.filter) ||
        isBooleanTableFilter(props.filter.column.filter)
      ) {
        let { options } = props.filter.column.filter;
        if (isBooleanTableFilter(props.filter.column.filter)) {
          // exclude null from options, "is empty" operator should be used instead
          options = options.filter((o) => o.value !== 'null');
        }
        return <SelectFilterRule {...commonFilterProps} options={options} />;
      }

      if (isDateTimeTableFilter(props.filter.column.filter)) {
        return <DateTimeFilterRule {...commonFilterProps} />;
      }

      return (
        <TextFilterRule
          {...commonFilterProps}
          number={isAsnTableFilter(props.filter.column.filter)}
          stepper={isNumberTableFilter(props.filter.column.filter)}
        />
      );
    }

    if (filterOperators.column.some((o) => o === operator)) {
      return (
        <ColumnFilterRule
          {...commonFilterProps}
          columns={props.columns}
          filterType={props.filter.column.filter.type}
          comparableColumns={props.filter.column.filter.comparableColumns}
          column={props.filter.column.key}
        />
      );
    }

    if (filterOperators.color.some((o) => o === operator)) {
      return (
        <ColorFilterRule
          {...commonFilterProps}
          column={props.filter.column.key}
        />
      );
    }

    if (filterOperators.empty.some((o) => o === operator)) {
      return null;
    }

    if (
      isLabelsArrayOperator(operator) &&
      isLabelsFilterColumn(props.filter.column)
    ) {
      return (
        <LabelsColumnFilterRule {...commonFilterProps} operator={operator} />
      );
    }

    if (isArrayContainsOperator(operator)) {
      return <TextFilterRule {...commonFilterProps} />;
    }

    if (isDateTimeTableFilter(props.filter.column.filter)) {
      return <DateTimeFilterForNowRule {...commonFilterProps} />;
    }

    stumble('Undefined filter component');
    return null;
  };

  const getArrayOperatorValue = (): ArrayLevelOperator | null => {
    if (!isArrayColumn(props.filter.column)) {
      stumble('getArrayOperatorValue called for non-array column');
      return null;
    }
    if (props.filter.arrayOperator !== undefined) {
      return props.filter.arrayOperator;
    }
    const operator = props.filter.operator;
    if (isLabelsArrayDropdownOperator(operator)) {
      return operator;
    }

    return null;
  };

  return (
    <div
      className={classNames(
        'filter-rule',
        props.draggable && 'filter-rule--nested',
      )}
    >
      <Select
        className='filter-rule__column'
        options={columns}
        value={props.filter.column}
        onChange={handleColumnChange}
        nonClearable
        searchable
        size='small'
        getOptionValue={makeGetSelectOptionValueFromObject('key', 'parent')}
      />
      {props.filter.column?.key === 'device.attributes' && (
        <div className='filter-rule__dynamic-field'>
          <TextInput
            hasError={props.filter.touched && !!props.filter.dynamicFieldError}
            value={props.filter.dynamicField}
            onChange={withTargetValue(handleDynamicFieldChange)}
            onBlur={handleBlur}
            placeholder='Attribute name'
            type='text'
            size='small'
          />
          {props.filter.touched && props.filter.dynamicFieldError && (
            <FormInputError error={props.filter.dynamicFieldError} />
          )}
        </div>
      )}
      {props.filter.column !== null && isArrayColumn(props.filter.column) && (
        <Select
          className='filter-rule__operator'
          options={arrayOperators}
          value={getArrayOperatorValue()}
          size='small'
          nonClearable
          onChange={handleArrayOperatorChange}
        />
      )}
      {operators !== null && !hideOperatorSelectForArrayTopLevelOperator && (
        <div className='filter-rule__operator'>
          <Select
            options={operators}
            value={
              props.filter.unsupportedOperatorError
                ? null
                : props.filter.operator
            }
            onChange={handleOperatorChange}
            nonClearable
            size='small'
            hasError={!!props.filter.unsupportedOperatorError}
          />
          {props.filter.unsupportedOperatorError && (
            <FormInputError error={props.filter.unsupportedOperatorError} />
          )}
        </div>
      )}
      {resolvedOperator &&
        pipe(
          getFilter(),
          nullable.chain((filter) => (
            <div className='filter-rule__value'>
              {filter}
              {valueErrorToRender && (
                <FormInputError error={valueErrorToRender} />
              )}
            </div>
          )),
        )}
      <Button
        type='button'
        variant='plain'
        icon='fa-trash-can'
        accent='negative'
        onClick={() => {
          props.onDelete(props.path);
        }}
      />
    </div>
  );
};
